import React, { useEffect, useState, useCallback } from 'react';
import toast from 'react-hot-toast';
import {
  diagnoseEmail, testEmail,
  getMasterSettings, updateMasterSettings, refreshExchangeRate,
} from '../../services/api';

/**
 * Configurações sistêmicas do master.
 *
 * Áreas transversais/técnicas da plataforma — separadas de "Faturamento",
 * que é somente cobrança. Aqui ficam:
 *   • Diagnóstico do serviço de e-mail (SMTP status, env, último erro, teste)
 *   • Configuração de custo do WhatsApp (Meta Cloud API) — usado para calcular
 *     o custo repassado nas faturas mensais.
 *
 * Daqui pra frente, este é o lugar onde devem entrar as demais configurações
 * técnicas/transversais do sistema.
 */
export default function MasterSystem() {
  // ── Email diagnóstico ───────────────────────────────────────────────────
  const [diag,        setDiag]        = useState(null);
  const [diagLoading, setDiagLoading] = useState(false);
  const [testTo,      setTestTo]      = useState('');
  const [testing,     setTesting]     = useState(false);

  // ── WhatsApp cost config ────────────────────────────────────────────────
  const [waCostUsd,      setWaCostUsd]      = useState('0.04');
  const [waExchangeRate, setWaExchangeRate] = useState('5.80');
  const [waExchangeAuto, setWaExchangeAuto] = useState(false);
  const [waExchangeMargin, setWaExchangeMargin] = useState('0.15');
  const [waLastPtax,     setWaLastPtax]     = useState(null);
  const [waLastUpdate,   setWaLastUpdate]   = useState(null);
  const [waLastSource,   setWaLastSource]   = useState(null);
  const [waLoaded,       setWaLoaded]       = useState(false);
  const [waSaving,       setWaSaving]       = useState(false);
  const [waRefreshing,   setWaRefreshing]   = useState(false);

  const loadDiag = useCallback(async () => {
    setDiagLoading(true);
    try {
      const r = await diagnoseEmail();
      setDiag(r.data.diagnose);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Erro ao diagnosticar e-mail');
    } finally {
      setDiagLoading(false);
    }
  }, []);

  const loadSettings = useCallback(async () => {
    try {
      const r = await getMasterSettings();
      const s = r.data.settings || {};
      setWaCostUsd(s.whatsapp_cost_usd || '0.04');
      setWaExchangeRate(s.whatsapp_exchange_rate || '5.80');
      setWaExchangeAuto(s.whatsapp_exchange_auto === '1');
      setWaExchangeMargin(s.whatsapp_exchange_margin_brl || '0.15');
      setWaLastPtax(s.whatsapp_exchange_last_ptax || null);
      setWaLastUpdate(s.whatsapp_exchange_last_update || null);
      setWaLastSource(s.whatsapp_exchange_last_source || null);
      setWaLoaded(true);
    } catch {
      /* noop — mantém defaults */
    }
  }, []);

  useEffect(() => {
    loadDiag();
    loadSettings();
  }, [loadDiag, loadSettings]);

  const onTest = async () => {
    if (!testTo) return toast.error('Informe um e-mail de destino');
    setTesting(true);
    try {
      const r = await testEmail(testTo);
      // O endpoint retorna 200 sempre (é diagnóstico). O sucesso/erro vem no
      // body como r.data.ok — evita duplo-toast com o interceptor global.
      if (r.data?.ok) {
        toast.success(r.data.message || 'E-mail enviado');
      } else {
        toast.error(r.data?.message || 'Falha ao enviar e-mail de teste');
      }
      loadDiag();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Falha ao enviar');
      loadDiag();
    } finally {
      setTesting(false);
    }
  };

  const handleWaSave = async () => {
    if (!waCostUsd || parseFloat(waCostUsd) < 0) return toast.error('Informe o custo por conversa (USD)');
    if (!waExchangeRate || parseFloat(waExchangeRate) <= 0) return toast.error('Informe a taxa de câmbio');
    if (waExchangeAuto && (isNaN(parseFloat(waExchangeMargin)) || parseFloat(waExchangeMargin) < 0)) {
      return toast.error('Informe uma margem válida (R$)');
    }
    setWaSaving(true);
    try {
      // Merge com o que já existe para não apagar chaves PIX.
      const current = (await getMasterSettings()).data.settings || {};
      await updateMasterSettings({ settings: {
        ...current,
        whatsapp_cost_usd: waCostUsd,
        whatsapp_exchange_rate: waExchangeRate,
        whatsapp_exchange_auto: waExchangeAuto ? '1' : '0',
        whatsapp_exchange_margin_brl: waExchangeMargin,
      }});
      toast.success('Configuração de WhatsApp salva!');
    } catch {
      toast.error('Erro ao salvar');
    } finally {
      setWaSaving(false);
    }
  };

  const handleWaRefreshPtax = async () => {
    setWaRefreshing(true);
    try {
      const r = await refreshExchangeRate();
      toast.success(r.data.message || 'Cotação atualizada');
      // Recarrega settings pra refletir nova taxa + timestamp
      await loadSettings();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Falha ao buscar PTAX');
    } finally {
      setWaRefreshing(false);
    }
  };

  return (
    <div className="page-body">
      <div className="page-header-row">
        <div className="page-header">
          <h2>Configurações do Sistema</h2>
          <p>Diagnósticos e ajustes técnicos transversais da plataforma</p>
        </div>
        <div className="page-actions">
          <button className="btn btn-secondary" onClick={loadDiag} disabled={diagLoading}>
            {diagLoading ? 'Verificando...' : '↻ Re-verificar'}
          </button>
        </div>
      </div>

      {/* Email diagnóstico */}
      <div className="card" style={{ marginBottom: 20, borderLeft: '3px solid var(--primary)' }}>
        <div className="card-header">
          <span className="card-title">✉️ Diagnóstico do Serviço de E-mail</span>
        </div>
        <div className="card-body">
          {diagLoading && !diag && <p>Verificando SMTP...</p>}
          {diag && (
            <>
              {/* Status badge */}
              <div style={{
                padding: '12px 16px',
                borderRadius: 8,
                marginBottom: 16,
                background: diag.verify?.ok ? '#f0fdf4' : '#fef2f2',
                border: `1px solid ${diag.verify?.ok ? '#86efac' : '#fca5a5'}`,
                color: diag.verify?.ok ? '#15803d' : '#991b1b',
              }}>
                <strong>{diag.verify?.ok ? '✓ SMTP Conectado' : '✗ SMTP Falhou'}</strong>
                {diag.verify?.error && (
                  <div style={{ fontSize: 13, marginTop: 4 }}>{diag.verify.error}</div>
                )}
                {diag.verify?.code && (
                  <div style={{ fontSize: 12, marginTop: 2, fontFamily: 'monospace' }}>
                    code: {diag.verify.code}
                    {diag.verify.command && ` | command: ${diag.verify.command}`}
                  </div>
                )}
                {diag.verify?.response && (
                  <div style={{ fontSize: 12, marginTop: 2, fontFamily: 'monospace' }}>
                    response: {diag.verify.response}
                  </div>
                )}
              </div>

              {/* Environment */}
              <h4 style={{ marginTop: 16, marginBottom: 8, fontSize: 13 }}>Variáveis de ambiente detectadas:</h4>
              <div style={{ background: 'var(--gray-50)', padding: 12, borderRadius: 6, fontFamily: 'monospace', fontSize: 12 }}>
                {Object.entries(diag.env).map(([k, v]) => (
                  <div key={k}>
                    <span style={{ color: 'var(--gray-500)' }}>{k}</span> = <span style={{ color: v ? 'var(--gray-900)' : '#dc2626' }}>{v ?? '(não configurado)'}</span>
                  </div>
                ))}
              </div>

              {/* Last error */}
              {diag.lastError && (
                <div style={{ marginTop: 16, padding: 12, background: '#fef2f2', border: '1px solid #fca5a5', borderRadius: 6, fontSize: 12 }}>
                  <strong style={{ color: '#991b1b' }}>Último erro de envio ({diag.lastError.timestamp}):</strong>
                  <div style={{ marginTop: 4, fontFamily: 'monospace' }}>
                    To: {diag.lastError.to}<br/>
                    Subject: {diag.lastError.subject}<br/>
                    Error: {diag.lastError.message}<br/>
                    Code: {diag.lastError.code || '(none)'}<br/>
                    {diag.lastError.response && <>Response: {diag.lastError.response}<br/></>}
                  </div>
                </div>
              )}

              {/* Test email */}
              <h4 style={{ marginTop: 20, marginBottom: 8, fontSize: 13 }}>Enviar e-mail de teste:</h4>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                <input
                  className="input"
                  type="email"
                  placeholder="destinatario@exemplo.com"
                  value={testTo}
                  onChange={e => setTestTo(e.target.value)}
                  style={{ flex: 1, minWidth: 200 }}
                />
                <button
                  className={`btn btn-primary${testing ? ' btn-loading' : ''}`}
                  onClick={onTest}
                  disabled={testing}>
                  {testing ? '' : 'Enviar teste'}
                </button>
              </div>
            </>
          )}
        </div>
      </div>

      {/* WhatsApp Cost */}
      <div className="card" style={{ marginBottom: 20, borderLeft: '3px solid var(--primary)' }}>
        <div className="card-header">
          <span className="card-title">💬 Custo WhatsApp (Meta Cloud API)</span>
        </div>
        <div className="card-body">
          <p style={{ fontSize: 13, color: 'var(--gray-500)', marginBottom: 16 }}>
            Estes valores são usados para calcular o custo de cada mensagem enviada via Meta Cloud API e adicionados automaticamente às faturas mensais dos clientes.
          </p>

          <div className="form-row">
            <div className="form-group">
              <label>Custo por conversa (USD)</label>
              <input
                type="number"
                step="0.0001"
                min="0"
                value={waCostUsd}
                onChange={e => setWaCostUsd(e.target.value)}
                placeholder="0.04"
                disabled={!waLoaded}
              />
              <small>Conversa Utility Meta: ~USD 0,04. Verifique em <strong>business.facebook.com</strong></small>
            </div>
            <div className="form-group">
              <label>Taxa de câmbio (USD → BRL)</label>
              <input
                type="number"
                step="0.01"
                min="1"
                value={waExchangeRate}
                onChange={e => setWaExchangeRate(e.target.value)}
                placeholder="5.80"
                disabled={!waLoaded || waExchangeAuto}
              />
              <small>{waExchangeAuto
                ? 'Atualizada automaticamente pelo BCB (PTAX + margem). Edite manualmente desmarcando a opção abaixo.'
                : 'Use um valor ligeiramente acima do câmbio atual para proteger da variação.'}
              </small>
            </div>
          </div>

          {/* Auto-update bloco */}
          <div style={{ background: 'var(--gray-50)', border: '1px solid var(--gray-200)', borderRadius: 8, padding: 14, marginTop: 4, marginBottom: 16 }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontWeight: 600, marginBottom: 8 }}>
              <input
                type="checkbox"
                checked={waExchangeAuto}
                onChange={e => setWaExchangeAuto(e.target.checked)}
                disabled={!waLoaded}
                style={{ width: 18, height: 18, cursor: 'pointer' }}
              />
              Atualizar câmbio automaticamente (PTAX / Banco Central)
            </label>
            <div style={{ fontSize: 12, color: 'var(--gray-500)', marginBottom: 12, paddingLeft: 26 }}>
              Busca diariamente às 14:15 BRT a cotação de fechamento do dólar no BCB e soma a margem abaixo.
              Exemplo: PTAX R$ 5,10 + margem R$ 0,15 = taxa aplicada R$ 5,25.
            </div>

            {waExchangeAuto && (
              <>
                <div className="form-row">
                  <div className="form-group" style={{ maxWidth: 260 }}>
                    <label>Margem sobre PTAX (R$)</label>
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      value={waExchangeMargin}
                      onChange={e => setWaExchangeMargin(e.target.value)}
                      placeholder="0.15"
                      disabled={!waLoaded}
                    />
                    <small>Colchão pra variação intraday + spread do banco.</small>
                  </div>
                </div>

                <div style={{ marginTop: 10, display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                  <button
                    type="button"
                    className={`btn btn-sm btn-secondary${waRefreshing ? ' btn-loading' : ''}`}
                    onClick={handleWaRefreshPtax}
                    disabled={waRefreshing || !waLoaded}
                  >
                    {waRefreshing ? 'Buscando...' : '↻ Atualizar agora'}
                  </button>
                  {waLastPtax && (
                    <div style={{ fontSize: 12, color: 'var(--gray-600)' }}>
                      Última PTAX: <strong>R$ {parseFloat(waLastPtax).toFixed(4)}</strong>
                      {' + '}margem R$ {parseFloat(waExchangeMargin || 0).toFixed(2)}
                      {' = '}<strong>R$ {(parseFloat(waLastPtax) + parseFloat(waExchangeMargin || 0)).toFixed(4)}</strong>
                    </div>
                  )}
                </div>
                {waLastUpdate && (
                  <div style={{ fontSize: 11, color: 'var(--gray-400)', marginTop: 6 }}>
                    Atualizado em {new Date(waLastUpdate).toLocaleString('pt-BR', { timeZone: 'America/Sao_Paulo' })}
                    {waLastSource && ` · origem: ${waLastSource === 'cron' ? 'cron diário' : waLastSource === 'manual_refresh' ? 'atualização manual' : waLastSource}`}
                  </div>
                )}
              </>
            )}
          </div>

          {waCostUsd && waExchangeRate && (
            <div style={{ background: 'var(--gray-50)', border: '1px solid var(--gray-200)', borderRadius: 6, padding: '8px 12px', fontSize: 12, marginBottom: 16 }}>
              Custo base: USD {parseFloat(waCostUsd || 0).toFixed(4)} × {parseFloat(waExchangeRate || 0).toFixed(2)} = <strong>R$ {(parseFloat(waCostUsd || 0) * parseFloat(waExchangeRate || 0)).toFixed(4)}</strong> por conversa · margem configurada por cliente (padrão 75%)
            </div>
          )}

          <button
            className={`btn btn-primary${waSaving ? ' btn-loading' : ''}`}
            disabled={waSaving || !waLoaded}
            onClick={handleWaSave}>
            {waSaving ? 'Salvando...' : '💾 Salvar custo do WhatsApp'}
          </button>
        </div>
      </div>
    </div>
  );
}
